import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RouteIcon from '@mui/icons-material/Route';
import BadgeIcon from '@mui/icons-material/Badge';
import type { NavigationItem, ModuleInfo } from '@core/router/types';
import { Permissions } from '@core/auth/permissions';

/**
 * Fleet Module Information
 *
 * Used by the ModuleSelector to display and navigate to this module.
 */
export const fleetModuleInfo: ModuleInfo = {
  id: 'vozni-park',
  labelKey: 'modules.fleet.name',
  icon: <DirectionsCarIcon />,
  basePath: '/vozni-park',
  accessPermission: Permissions.Fleet.Vehicles.Read, // Base Fleet permission
  order: 1,
};

/**
 * Fleet Module Navigation Items
 *
 * Hierarchical sidebar navigation structure for the Fleet module.
 *
 * Structure:
 * - Vozila (Vehicles) - All users can see
 *   - Sva vozila (All vehicles) - Shows all vehicles for managers, only available for standard users
 *   - Zahtevi (Requests)
 *
 * - Gorivo (Fuel) - Top level - Admin/Manager only
 *
 * - Operacije (Operations) - Admin/Manager only
 *   - Servisi (Services)
 *   - Gume (Tires)
 *   - Registracije (Registrations)
 *
 * - Ugovori (Contracts) - Admin/Manager only
 *   - Aktivni ugovori (Active contracts)
 *   - Dobavljači (Providers)
 *
 * - Putni nalozi (Travel Orders) - All users (filtered by backend)
 * - Korisnici (Fleet Users) - Manager/Admin only
 * - Izvještaji (Reports) - Admin/Manager only
 */
export const fleetNavigation: NavigationItem[] = [
  // ============================================
  // VOZILA GROUP - All users can see
  // ============================================
  {
    label: 'Vozila',
    labelKey: 'fleets.nav.vehicles',
    // No path - this is an expandable group, not a navigable item
    icon: <DirectionsCarIcon />,
    permission: Permissions.Fleet.Vehicles.Read,
    children: [
      {
        label: 'Sva vozila',
        labelKey: 'fleets.nav.allVehicles',
        path: '/vozni-park/vozila',
        icon: <ListAltIcon />,
        permission: Permissions.Fleet.Vehicles.Read,
      },
      {
        label: 'Zahtevi',
        labelKey: 'fleets.nav.requests',
        path: '/vozni-park/vozila/zahtevi',
        icon: <PostAddIcon />,
        permission: Permissions.Fleet.Requests.Read,
      },
    ],
  },

  // ============================================
  // GORIVO - Top level - Admin/Manager only
  // ============================================
  {
    label: 'Gorivo',
    labelKey: 'fleets.nav.fuel',
    path: '/vozni-park/gorivo',
    icon: <LocalGasStationIcon />,
    permission: Permissions.Fleet.Operations.Read,
  },

  // ============================================
  // OPERACIJE GROUP - Admin/Manager only
  // ============================================
  {
    label: 'Operacije',
    labelKey: 'fleets.nav.operations',
    // No path - expandable group only
    icon: <SettingsIcon />,
    permission: Permissions.Fleet.Operations.Read,
    children: [
      {
        label: 'Servisi',
        labelKey: 'fleets.nav.services',
        path: '/vozni-park/servisi',
        icon: <BuildIcon />,
        permission: Permissions.Fleet.Operations.Read,
      },
      {
        label: 'Gume',
        labelKey: 'fleets.nav.tires',
        path: '/vozni-park/nabavka-guma',
        icon: <TireRepairIcon />,
        permission: Permissions.Fleet.Operations.Read,
      },
      {
        label: 'Registracije',
        labelKey: 'fleets.nav.registrations',
        path: '/vozni-park/registracije',
        icon: <BadgeIcon />,
        permission: Permissions.Fleet.Operations.Read,
      },
    ],
  },

  // ============================================
  // EVIDENCIJA GUMA - Tire tracking (new feature)
  // ============================================
  {
    label: 'Evidencija guma',
    labelKey: 'fleets.nav.tireTracking',
    path: '/vozni-park/gume',
    icon: <TireRepairIcon />,
    permission: Permissions.Fleet.Tires.Read,
  },

  // ============================================
  // UGOVORI GROUP - Admin/Manager only
  // ============================================
  {
    label: 'Ugovori',
    labelKey: 'fleets.nav.contracts',
    // No path - expandable group only
    icon: <DescriptionIcon />,
    permission: Permissions.Fleet.Contracts.Read,
    children: [
      {
        label: 'Aktivni ugovori',
        labelKey: 'fleets.nav.activeContracts',
        path: '/vozni-park/ugovori',
        icon: <DescriptionIcon />,
        permission: Permissions.Fleet.Contracts.Read,
      },
      {
        label: 'Dobavljači',
        labelKey: 'fleets.nav.providers',
        path: '/vozni-park/dobavljaci',
        icon: <HandshakeIcon />,
        permission: Permissions.Fleet.Providers.Read,
      },
    ],
  },

  // ============================================
  // VOŽNJE (Trips) - All users with tab-based filtering
  // ============================================
  {
    label: 'Vožnje',
    labelKey: 'fleets.nav.travelOrders',
    path: '/vozni-park/voznje',
    icon: <RouteIcon />,
    permission: Permissions.Fleet.TravelOrders.Read,
    // Page has tabs: "Moje vožnje" (all users) and "Sve vožnje" (managers only)
    // FleetUser defaults to "My Trips" tab, Manager/Director defaults to "All Trips" tab
  },

  // ============================================
  // PUTNI NALOZI (Periods) - Managers/Directors only
  // ============================================
  {
    label: 'Putni nalozi',
    labelKey: 'fleets.nav.travelOrderPeriods',
    path: '/vozni-park/putni-nalozi',
    icon: <CalendarMonthIcon />,
    // No permission for now - using requireAuth in routes
    // Can add permission later: permission: Permissions.Fleet.TravelOrders.Manage,
  },

  // ============================================
  // KORISNICI - Manager/Admin only
  // ============================================
  {
    label: 'Korisnici',
    labelKey: 'fleets.nav.fleetUsers',
    path: '/vozni-park/korisnici',
    icon: <PeopleIcon />,
    permission: Permissions.Fleet.FleetUsers.Read,
    // Managers can see all fleet users and their requests/travel orders
  },

  // ============================================
  // MOJ NALOG - All authenticated users
  // ============================================
  {
    label: 'Moj nalog',
    labelKey: 'fleets.nav.myAccount',
    path: '/vozni-park/profil',
    icon: <AccountCircleIcon />,
    dividerBefore: true,
    // No permission - all authenticated users can access their account
  },

  // ============================================
  // IZVJEŠTAJI - Admin/Manager only
  // ============================================
//   {
//     label: 'Izvještaji',
//     path: '/vozni-park/izvjestaji',
//     icon: <BarChartIcon />,
//     permission: Permissions.Fleet.Vehicles.Read, // Base permission for reports
//   },
];
